import classNames from 'classnames';
import omit from 'lodash.omit';
import PropTypes from 'prop-types';
// import React from 'react';
import {defineMessages, FormattedMessage, injectIntl, intlShape} from 'react-intl';
import {connect} from 'react-redux';
import MediaQuery from 'react-responsive';
import {Tab, Tabs, TabList, TabPanel} from 'react-tabs';
import tabStyles from 'react-tabs/style/react-tabs.css';
import VM from 'scratch-vm';
import Renderer from 'scratch-render';

import Blocks from '../../containers/blocks.jsx';
import CostumeTab from '../../containers/costume-tab.jsx';
import TargetPane from '../../containers/target-pane.jsx';
import SoundTab from '../../containers/sound-tab.jsx';
import StageWrapper from '../../containers/stage-wrapper.jsx';
import Loader from '../loader/loader.jsx';
import Box from '../box/box.jsx';
import MenuBar from '../menu-bar/menu-bar.jsx';
import CostumeLibrary from '../../containers/costume-library.jsx';
import BackdropLibrary from '../../containers/backdrop-library.jsx';
import Watermark from '../../containers/watermark.jsx';

import Backpack from '../../containers/backpack.jsx';
import WebGlModal from '../../containers/webgl-modal.jsx';
import TipsLibrary from '../../containers/tips-library.jsx';
import Cards from '../../containers/cards.jsx';
import Alerts from '../../containers/alerts.jsx';
import DragLayer from '../../containers/drag-layer.jsx';
import ConnectionModal from '../../containers/connection-modal.jsx';
import TelemetryModal from '../telemetry-modal/telemetry-modal.jsx';

import layout, {STAGE_SIZE_MODES} from '../../lib/layout-constants';
import {resolveStageSize} from '../../lib/screen-utils';
import {themeMap} from '../../lib/themes';

import styles from './gui.css';
import addExtensionIcon from './icon--extensions.svg';
import codeIcon from './icon--code.svg';
import costumesIcon from './icon--costumes.svg';
import soundsIcon from './icon--sounds.svg';
import DebugModal from '../debug-modal/debug-modal.jsx';

import {buildImageUrl, buildExampleUrl} from '../../lib/lesson-utils';

import LessonPanel from '../lesson-panel/lesson-panel.jsx';
// import React, {useState} from 'react';
import React, {useState, useEffect} from 'react';
// import lessons from '../../lib/lessons';
import {getLessons} from '../../lib/lessons';

const messages = defineMessages({
    addExtension: {
        id: 'gui.gui.addExtension',
        description: 'Button to add an extension in the target pane',
        defaultMessage: 'Add Extension'
    },
    confirmResetProgress: {
        id: 'gui.gui.confirmResetProgress',
        description: 'Confirmation message before resetting lesson progress',
        defaultMessage: 'Reset completion checks for all lessons?'
    },
    confirmLoadExample: {
        id: 'gui.gui.confirmLoadExample',
        description: 'Confirmation message before loading an example project',
        defaultMessage: 'Loading this code will erase your current work. Continue?'
    },
    loadExampleFailed: {
        id: 'gui.gui.loadExampleFailed',
        description: 'Alert message when loading an example project fails',
        defaultMessage: 'Failed to load the example code.'
    },
        connectionPromptQuestion: {
        id: 'gui.gui.connectionPromptQuestion',
        description: 'Question asking user which connection type they use',
        defaultMessage: 'Are you using Bluetooth or a dongle?'
    },
    connectionPromptBluetooth: {
        id: 'gui.gui.connectionPromptBluetooth',
        description: 'Button label for bluetooth connection choice',
        defaultMessage: 'Bluetooth'
    },
    connectionPromptDongle: {
        id: 'gui.gui.connectionPromptDongle',
        description: 'Button label for dongle connection choice',
        defaultMessage: 'Dongle'
    },
    connectionPromptCancel: {
        id: 'gui.gui.connectionPromptCancel',
        description: 'Button label to cancel the connection prompt',
        defaultMessage: 'Cancel'
    }
});

// Cache this value to only retrieve it once the first time.
// Assume that it doesn't change for a session.
let isRendererSupported = null;

const PROGRESS_STORAGE_KEY = 'lessonProgress';

const getInitialProgress = () => {
    try {
        const saved = window.localStorage.getItem(PROGRESS_STORAGE_KEY);
        return saved ? JSON.parse(saved) : {};
    } catch (e) {
        return {};
    }
};

const getInitialLessonPanelVisible = () => {
    try {
        const saved = window.localStorage.getItem('lessonPanelVisible');
        return saved === null ? true : saved === 'true';
    } catch (e) {
        return true;
    }
};

const GUIComponent = props => {
    const {
        accountNavOpen,
        activeTabIndex,
        alertsVisible,
        authorId,
        authorThumbnailUrl,
        authorUsername,
        basePath,
        backdropLibraryVisible,
        backpackHost,
        backpackVisible,
        blocksId,
        blocksTabVisible,
        cardsVisible,
        canChangeLanguage,
        canChangeTheme,
        canCreateNew,
        canEditTitle,
        canManageFiles,
        canRemix,
        canSave,
        canCreateCopy,
        canShare,
        canUseCloud,
        children,
        connectionModalVisible,
        costumeLibraryVisible,
        costumesTabVisible,
        debugModalVisible,
        enableCommunity,
        intl,
        isCreating,
        isFullScreen,
        isPlayerOnly,
        isRtl,
        isShared,
        isTelemetryEnabled,
        isTotallyNormal,
        loading,
        logo,
        renderLogin,
        onClickAbout,
        onClickAccountNav,
        onCloseAccountNav,
        onLogOut,
        onOpenRegistration,
        onToggleLoginOpen,
        onActivateCostumesTab,
        onActivateSoundsTab,
        onActivateTab,
        onClickLogo,
        onExtensionButtonClick,
        onProjectTelemetryEvent,
        onRequestCloseBackdropLibrary,
        onRequestCloseCostumeLibrary,
        onRequestCloseDebugModal,
        onRequestCloseTelemetryModal,
        onSeeCommunity,
        onShare,
        onShowPrivacyPolicy,
        onStartSelectingFileUpload,
        onTelemetryModalCancel,
        onTelemetryModalOptIn,
        onTelemetryModalOptOut,
        showComingSoon,
        soundsTabVisible,
        stageSizeMode,
        targetIsStage,
        telemetryModalVisible,
        theme,
        tipsLibraryVisible,
        vm,
        ...componentProps
    } = omit(props, 'dispatch');

    const [lessonPanelVisible, setLessonPanelVisible] = useState(getInitialLessonPanelVisible);
    const [shouldPulse, setShouldPulse] = useState(true);
    const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [lessonMenuOpen, setLessonMenuOpen] = useState(false);
    const [completedSteps, setCompletedSteps] = useState(getInitialProgress);

    const currentLocale = intl.locale; // 'ko', 'en', 'ja', 'zh-cn' 등 뭐가 오든
// console.log('현재 언어:', currentLocale);

    const [connectionPromptOpen, setConnectionPromptOpen] = useState(false);
    const [pendingExampleBlock, setPendingExampleBlock] = useState(null);


    const lessons = getLessons(currentLocale);
// console.log('lessons 배열:', lessons);        // 추가
// console.log('lessons 길이:', lessons.length);  // 추가

const handleToggleComplete = () => {
    const lessonId = currentLesson.id;
    const stepId = currentStep.id;

    setCompletedSteps(prev => {
        const lessonProgress = prev[lessonId] || [];
        const isDone = lessonProgress.includes(stepId);

        const updated = {
            ...prev,
            [lessonId]: isDone
                ? lessonProgress.filter(id => id !== stepId)
                : [...lessonProgress, stepId]
        };

        try {
            window.localStorage.setItem(PROGRESS_STORAGE_KEY, JSON.stringify(updated));
        } catch (e) {
            // 무시
        }

        return updated;
    });
};


const handleResetProgress = () => {
    const isConfirmed = window.confirm(intl.formatMessage(messages.confirmResetProgress));
    if (!isConfirmed) return;

    setCompletedSteps({});
    try {
        window.localStorage.removeItem(PROGRESS_STORAGE_KEY);
    } catch (e) {
        // 무시
    }
};



    const currentLesson = lessons[currentLessonIndex];

    const currentSteps = currentLesson.steps;
    const currentStep = currentSteps[currentStepIndex];

    const hasNextStep = currentStepIndex < currentSteps.length - 1;
    const hasPrevStep = currentStepIndex > 0;


    const handleNextStep = () => {
        if (hasNextStep) setCurrentStepIndex(currentStepIndex + 1);
    };
    const handlePrevStep = () => {
        if (hasPrevStep) setCurrentStepIndex(currentStepIndex - 1);
    };

const handleGoToStep = index => {          // ← 이 함수가 실제로 있는지
    setCurrentStepIndex(index);
};

useEffect(() => {
    try {
        window.localStorage.setItem('lessonPanelVisible', String(lessonPanelVisible));
    } catch (e) {
        // 무시
    }
}, [lessonPanelVisible]);


// useEffect(() => {
//     const isBluetooth = vm.extensionManager.isExtensionLoaded('zumiAIB');
//     const isDongle = vm.extensionManager.isExtensionLoaded('zumiAIS');

//     console.log('블루투스 확장 로드됨:', isBluetooth);
//     console.log('동글 확장 로드됨:', isDongle);

//     if (isBluetooth && !isDongle) {
//         console.log('→ 결과: 블루투스로 연결됨');
//     } else if (isDongle && !isBluetooth) {
//         console.log('→ 결과: 동글로 연결됨');
//     } else if (isBluetooth && isDongle) {
//         console.log('→ 결과: 둘 다 로드됨');
//     } else {
//         console.log('→ 결과: 둘 다 아님(확장 미추가)');
//     }
// }, [vm]);




const handleGoToLesson = (lessonIndex, stepIndex = 0) => {
    setCurrentLessonIndex(lessonIndex);
    setCurrentStepIndex(stepIndex);
};

const handleLoadExampleByConnection = block => {
    // 존재하는 src_* 필드가 몇 개인지 확인
    const availableSources = ['src_basic', 'src_dongle', 'src_bt']
        .filter(key => block[key]);

    // 딱 하나만 있으면 확장 상태 무관하게 그걸로 바로 실행
    if (availableSources.length === 1) {
        handleLoadExample(buildExampleUrl(currentLesson.id, block[availableSources[0]]));
        return;
    }

    // 여러 개 있을 때만 확장 상태를 확인해서 분기
    const isBluetooth = vm.extensionManager.isExtensionLoaded('zumiAIB');
    const isDongle = vm.extensionManager.isExtensionLoaded('zumiAIS');

    if (isDongle && block.src_dongle) {
        handleLoadExample(buildExampleUrl(currentLesson.id, block.src_dongle));
        return;
    }

    if (isBluetooth && block.src_bt) {
        handleLoadExample(buildExampleUrl(currentLesson.id, block.src_bt));
        return;
    }

    // 확장이 둘 다 없거나, 확장은 있는데 그에 맞는 src가 없는 경우 → 물어보기
    setPendingExampleBlock(block);
    setConnectionPromptOpen(true);
};

const handleSelectConnectionType = type => {
    setConnectionPromptOpen(false);
    if (!pendingExampleBlock) return;

    const filename = type === 'dongle' ? pendingExampleBlock.src_dongle : pendingExampleBlock.src_bt;
    handleLoadExample(buildExampleUrl(currentLesson.id, filename));
    setPendingExampleBlock(null);
};

const handleLoadExample = projectUrl => {
    if (!projectUrl) return;

    const isConfirmed = window.confirm(intl.formatMessage(messages.confirmLoadExample));
    if (!isConfirmed) return;

    fetch(projectUrl)
        .then(response => response.arrayBuffer())
        .then(arrayBuffer => vm.loadProject(arrayBuffer))
        .catch(err => {
            console.error('예시 프로젝트 로드 실패:', err); // 이건 개발자용 콘솔 로그라 안 바꿔도 됨
            window.alert(intl.formatMessage(messages.loadExampleFailed));
        });
};

    if (children) {
        return <Box {...componentProps}>{children}</Box>;
    }

    const tabClassNames = {
        tabs: styles.tabs,
        tab: classNames(tabStyles.reactTabsTab, styles.tab),
        tabList: classNames(tabStyles.reactTabsTabList, styles.tabList),
        tabPanel: classNames(tabStyles.reactTabsTabPanel, styles.tabPanel),
        tabPanelSelected: classNames(tabStyles.reactTabsTabPanelSelected, styles.isSelected),
        tabSelected: classNames(tabStyles.reactTabsTabSelected, styles.isSelected)
    };

    if (isRendererSupported === null) {
        isRendererSupported = Renderer.isSupported();
    }

    return (<MediaQuery minWidth={layout.fullSizeMinWidth}>{isFullSize => {
        const stageSize = resolveStageSize(stageSizeMode, isFullSize);

        return isPlayerOnly ? (
            <StageWrapper
                isFullScreen={isFullScreen}
                isRendererSupported={isRendererSupported}
                isRtl={isRtl}
                loading={loading}
                stageSize={STAGE_SIZE_MODES.large}
                vm={vm}
            >
                {alertsVisible ? (
                    <Alerts className={styles.alertsContainer} />
                ) : null}
            </StageWrapper>
        ) : (
            <Box
                className={styles.pageWrapper}
                dir={isRtl ? 'rtl' : 'ltr'}
                {...componentProps}
            >
                {telemetryModalVisible ? (
                    <TelemetryModal
                        isRtl={isRtl}
                        isTelemetryEnabled={isTelemetryEnabled}
                        onCancel={onTelemetryModalCancel}
                        onOptIn={onTelemetryModalOptIn}
                        onOptOut={onTelemetryModalOptOut}
                        onRequestClose={onRequestCloseTelemetryModal}
                        onShowPrivacyPolicy={onShowPrivacyPolicy}
                    />
                ) : null}
                {loading ? (
                    <Loader />
                ) : null}
                {isCreating ? (
                    <Loader messageId="gui.loader.creating" />
                ) : null}
                {isRendererSupported ? null : (
                    <WebGlModal isRtl={isRtl} />
                )}
                {tipsLibraryVisible ? (
                    <TipsLibrary />
                ) : null}
                {cardsVisible ? (
                    <Cards />
                ) : null}
                {alertsVisible ? (
                    <Alerts className={styles.alertsContainer} />
                ) : null}
                {connectionModalVisible ? (
                    <ConnectionModal
                        vm={vm}
                    />
                ) : null}
                {costumeLibraryVisible ? (
                    <CostumeLibrary
                        vm={vm}
                        onRequestClose={onRequestCloseCostumeLibrary}
                    />
                ) : null}

                {connectionPromptOpen && (
                    <div className={styles.connectionPromptOverlay}>
                        <div className={styles.connectionPromptBox}>
                            <p>{intl.formatMessage(messages.connectionPromptQuestion)}</p>
                            <div className={styles.connectionPromptButtons}>
                                <button onClick={() => handleSelectConnectionType('bluetooth')}>
                                    {intl.formatMessage(messages.connectionPromptBluetooth)}
                                </button>
                                <button onClick={() => handleSelectConnectionType('dongle')}>
                                    {intl.formatMessage(messages.connectionPromptDongle)}
                                </button>
                            </div>
                            <button
                                className={styles.connectionPromptCancel}
                                onClick={() => setConnectionPromptOpen(false)}
                            >
                                {intl.formatMessage(messages.connectionPromptCancel)}
                            </button>
                        </div>
                    </div>
                )}


                {<DebugModal
                    isOpen={debugModalVisible}
                    onClose={onRequestCloseDebugModal}
                />}
                {backdropLibraryVisible ? (
                    <BackdropLibrary
                        vm={vm}
                        onRequestClose={onRequestCloseBackdropLibrary}
                    />
                ) : null}
                <MenuBar
                    accountNavOpen={accountNavOpen}
                    authorId={authorId}
                    authorThumbnailUrl={authorThumbnailUrl}
                    authorUsername={authorUsername}
                    canChangeLanguage={canChangeLanguage}
                    canChangeTheme={canChangeTheme}
                    canCreateCopy={canCreateCopy}
                    canCreateNew={canCreateNew}
                    canEditTitle={canEditTitle}
                    canManageFiles={canManageFiles}
                    canRemix={canRemix}
                    canSave={canSave}
                    canShare={canShare}
                    className={styles.menuBarPosition}
                    enableCommunity={enableCommunity}
                    isShared={isShared}
                    isTotallyNormal={isTotallyNormal}
                    logo={logo}
                    renderLogin={renderLogin}
                    showComingSoon={showComingSoon}
                    onClickAbout={onClickAbout}
                    onClickAccountNav={onClickAccountNav}
                    onClickLogo={onClickLogo}
                    onCloseAccountNav={onCloseAccountNav}
                    onLogOut={onLogOut}
                    onOpenRegistration={onOpenRegistration}
                    onProjectTelemetryEvent={onProjectTelemetryEvent}
                    onSeeCommunity={onSeeCommunity}
                    onShare={onShare}
                    onStartSelectingFileUpload={onStartSelectingFileUpload}
                    onToggleLoginOpen={onToggleLoginOpen}

                    lessonMenuOpen={lessonMenuOpen}
                    lessonPanelVisible={lessonPanelVisible}
                    onClickLesson={() => setLessonMenuOpen(true)}
                    onRequestCloseLesson={() => setLessonMenuOpen(false)}
                    onToggleLessonPanel={() => setLessonPanelVisible(!lessonPanelVisible)}
                    onResetLessonProgress={handleResetProgress}
                />
                <Box className={styles.bodyWrapper}>
                    <Box className={styles.flexWrapper}>


                        {/* {lessonPanelVisible ? (
                            <LessonPanel
                                lesson={currentLesson}
                                onToggle={onToggleLessonPanel}
                            />
                        ) : null} */}

                        {/* <LessonPanel isVisible={true} /> */}

                        <LessonPanel
                            isVisible={lessonPanelVisible}
                            shouldPulse={shouldPulse}
                            onToggle={() => {
                                setLessonPanelVisible(!lessonPanelVisible);
                                setShouldPulse(false);
                            }}
                            lesson={currentLesson}
                            lessons={lessons}
                            currentLessonIndex={currentLessonIndex}
                            onGoToLesson={handleGoToLesson}
                            steps={currentSteps}
                            currentStepIndex={currentStepIndex}
                            currentStep={currentStep}
                            hasNext={hasNextStep}
                            hasPrev={hasPrevStep}
                            onNext={handleNextStep}
                            onPrev={handlePrevStep}
                            onGoToStep={handleGoToStep}
                            onLoadExample={handleLoadExample}
                            onLoadExampleByConnection={handleLoadExampleByConnection}
                            completedSteps={completedSteps}
                            onToggleComplete={handleToggleComplete}
                        />

                        <Box className={styles.editorWrapper}>
                            <Tabs
                                forceRenderTabPanel
                                className={tabClassNames.tabs}
                                selectedIndex={activeTabIndex}
                                selectedTabClassName={tabClassNames.tabSelected}
                                selectedTabPanelClassName={tabClassNames.tabPanelSelected}
                                onSelect={onActivateTab}
                            >
                                <TabList className={tabClassNames.tabList}>
                                    <Tab className={tabClassNames.tab}>
                                        <img
                                            draggable={false}
                                            src={codeIcon}
                                        />
                                        <FormattedMessage
                                            defaultMessage="Code"
                                            description="Button to get to the code panel"
                                            id="gui.gui.codeTab"
                                        />
                                    </Tab>
                                    <Tab
                                        className={tabClassNames.tab}
                                        onClick={onActivateCostumesTab}
                                    >
                                        <img
                                            draggable={false}
                                            src={costumesIcon}
                                        />
                                        {targetIsStage ? (
                                            <FormattedMessage
                                                defaultMessage="Backdrops"
                                                description="Button to get to the backdrops panel"
                                                id="gui.gui.backdropsTab"
                                            />
                                        ) : (
                                            <FormattedMessage
                                                defaultMessage="Costumes"
                                                description="Button to get to the costumes panel"
                                                id="gui.gui.costumesTab"
                                            />
                                        )}
                                    </Tab>
                                    <Tab
                                        className={tabClassNames.tab}
                                        onClick={onActivateSoundsTab}
                                    >
                                        <img
                                            draggable={false}
                                            src={soundsIcon}
                                        />
                                        <FormattedMessage
                                            defaultMessage="Sounds"
                                            description="Button to get to the sounds panel"
                                            id="gui.gui.soundsTab"
                                        />
                                    </Tab>
                                </TabList>
                                <TabPanel className={tabClassNames.tabPanel}>
                                    <Box className={styles.blocksWrapper}>
                                        <Blocks
                                            key={`${blocksId}/${theme}`}
                                            canUseCloud={canUseCloud}
                                            grow={1}
                                            isVisible={blocksTabVisible}
                                            options={{
                                                media: `${basePath}static/${themeMap[theme].blocksMediaFolder}/`
                                            }}
                                            stageSize={stageSize}
                                            theme={theme}
                                            vm={vm}
                                        />
                                    </Box>
                                    <Box className={styles.extensionButtonContainer}>
                                        <button
                                            className={styles.extensionButton}
                                            title={intl.formatMessage(messages.addExtension)}
                                            onClick={onExtensionButtonClick}
                                        >
                                            <img
                                                className={styles.extensionButtonIcon}
                                                draggable={false}
                                                src={addExtensionIcon}
                                            />
                                        </button>
                                    </Box>
                                    <Box className={styles.watermark}>
                                        <Watermark />
                                    </Box>
                                </TabPanel>
                                <TabPanel className={tabClassNames.tabPanel}>
                                    {costumesTabVisible ? <CostumeTab vm={vm} /> : null}
                                </TabPanel>
                                <TabPanel className={tabClassNames.tabPanel}>
                                    {soundsTabVisible ? <SoundTab vm={vm} /> : null}
                                </TabPanel>
                            </Tabs>
                            {/* {backpackVisible ? (
                                <Backpack host={backpackHost} />
                            ) : null} */}
                        </Box>

                        <Box className={classNames(styles.stageAndTargetWrapper, styles[stageSize])}>
                            <StageWrapper
                                isFullScreen={isFullScreen}
                                isRendererSupported={isRendererSupported}
                                isRtl={isRtl}
                                stageSize={stageSize}
                                vm={vm}
                            />
                            <Box className={styles.targetWrapper}>
                                <TargetPane
                                    stageSize={stageSize}
                                    vm={vm}
                                />
                            </Box>
                        </Box>


                    </Box>
                </Box>
                <DragLayer />
            </Box>
        );
    }}</MediaQuery>);
};

GUIComponent.propTypes = {
    accountNavOpen: PropTypes.bool,
    activeTabIndex: PropTypes.number,
    authorId: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]), // can be false
    authorThumbnailUrl: PropTypes.string,
    authorUsername: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]), // can be false
    backdropLibraryVisible: PropTypes.bool,
    backpackHost: PropTypes.string,
    backpackVisible: PropTypes.bool,
    basePath: PropTypes.string,
    blocksTabVisible: PropTypes.bool,
    blocksId: PropTypes.string,
    canChangeLanguage: PropTypes.bool,
    canChangeTheme: PropTypes.bool,
    canCreateCopy: PropTypes.bool,
    canCreateNew: PropTypes.bool,
    canEditTitle: PropTypes.bool,
    canManageFiles: PropTypes.bool,
    canRemix: PropTypes.bool,
    canSave: PropTypes.bool,
    canShare: PropTypes.bool,
    canUseCloud: PropTypes.bool,
    cardsVisible: PropTypes.bool,
    children: PropTypes.node,
    costumeLibraryVisible: PropTypes.bool,
    costumesTabVisible: PropTypes.bool,
    debugModalVisible: PropTypes.bool,
    enableCommunity: PropTypes.bool,
    intl: intlShape.isRequired,
    isCreating: PropTypes.bool,
    isFullScreen: PropTypes.bool,
    isPlayerOnly: PropTypes.bool,
    isRtl: PropTypes.bool,
    isShared: PropTypes.bool,
    isTotallyNormal: PropTypes.bool,
    loading: PropTypes.bool,
    logo: PropTypes.string,
    onActivateCostumesTab: PropTypes.func,
    onActivateSoundsTab: PropTypes.func,
    onActivateTab: PropTypes.func,
    onClickAccountNav: PropTypes.func,
    onClickLogo: PropTypes.func,
    onCloseAccountNav: PropTypes.func,
    onExtensionButtonClick: PropTypes.func,
    onLogOut: PropTypes.func,
    onOpenRegistration: PropTypes.func,
    onRequestCloseBackdropLibrary: PropTypes.func,
    onRequestCloseCostumeLibrary: PropTypes.func,
    onRequestCloseDebugModal: PropTypes.func,
    onRequestCloseTelemetryModal: PropTypes.func,
    onSeeCommunity: PropTypes.func,
    onShare: PropTypes.func,
    onShowPrivacyPolicy: PropTypes.func,
    onStartSelectingFileUpload: PropTypes.func,
    onTabSelect: PropTypes.func,
    onTelemetryModalCancel: PropTypes.func,
    onTelemetryModalOptIn: PropTypes.func,
    onTelemetryModalOptOut: PropTypes.func,
    onToggleLoginOpen: PropTypes.func,
    renderLogin: PropTypes.func,
    showComingSoon: PropTypes.bool,
    soundsTabVisible: PropTypes.bool,
    stageSizeMode: PropTypes.oneOf(Object.keys(STAGE_SIZE_MODES)),
    targetIsStage: PropTypes.bool,
    telemetryModalVisible: PropTypes.bool,
    theme: PropTypes.string,
    tipsLibraryVisible: PropTypes.bool,
    vm: PropTypes.instanceOf(VM).isRequired
};
GUIComponent.defaultProps = {
    backpackHost: null,
    backpackVisible: false,
    basePath: './',
    blocksId: 'original',
    canChangeLanguage: true,
    canChangeTheme: true,
    canCreateNew: false,
    canEditTitle: false,
    canManageFiles: true,
    canRemix: false,
    canSave: false,
    canCreateCopy: false,
    canShare: false,
    canUseCloud: false,
    enableCommunity: false,
    isCreating: false,
    isShared: false,
    isTotallyNormal: false,
    loading: false,
    showComingSoon: false,
    stageSizeMode: STAGE_SIZE_MODES.large
};

const mapStateToProps = state => ({
    // This is the button's mode, as opposed to the actual current state
    blocksId: state.scratchGui.timeTravel.year.toString(),
    stageSizeMode: state.scratchGui.stageSize.stageSize,
    theme: state.scratchGui.theme.theme
});

export default injectIntl(connect(
    mapStateToProps
)(GUIComponent));
